/** A* pathfinding on the tilemap collision grid with 8-directional movement and no corner-cutting. */
import Phaser from 'phaser';
import type { Position } from './Types';
import { worldToTile, tileToWorld } from './Math';
import { tileBlocksMovement, BUFFER_TILE_COST_MULTIPLIER } from '../ai/constants';

/** Why a pathfind search terminated. */
export type PathfindOutcome =
  | 'found'            // path to goal discovered
  | 'budget_exhausted' // ran out of search nodes before reaching goal
  | 'unreachable'      // open set emptied — no path exists
  | 'goal_blocked'     // goal tile is a wall or void
  | 'same_tile';       // start and goal are the same tile

/** Result of an A* pathfind search, including diagnostics. */
export interface PathfindResult {
  path: Position[] | null;
  nodesSearched: number;
  outcome: PathfindOutcome;
}

interface PathNode {
  x: number;
  y: number;
  g: number;
  h: number;
  f: number;
  parent: PathNode | null;
}

/** Deterministic noise from tile coords + seed, returns 0..1. Used for path cost perturbation. */
function perturbNoise(tx: number, ty: number, seed: number): number {
  let h = (seed ^ (tx * 374761393 + ty * 668265263)) | 0;
  h = ((h ^ (h >>> 13)) * 1274126177) | 0;
  h = h ^ (h >>> 16);
  return (h & 0xFFFF) / 0xFFFF;
}

// #24: Numeric key from tile coordinates — avoids string allocation per node
function tileKey(x: number, y: number): number {
  // Shift x by 16 bits. Supports maps up to 65535 tiles wide/tall.
  return (x << 16) | (y & 0xFFFF);
}

/**
 * A* pathfinder operating on the tilemap collision grid.
 * Returns a list of tile-center world positions from start to goal.
 *
 * Buffer-blocked tiles (building buffer zones) are walkable but carry a cost
 * penalty (BUFFER_TILE_COST_MULTIPLIER). This lets enemies navigate through
 * narrow corridors between buildings while still preferring open paths when
 * the detour is short.
 */
export function findPath(
  groundLayer: Phaser.Tilemaps.TilemapLayer,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  maxSearchNodes: number = 200,
  pathSeed?: number,
  perturbStrength?: number,
): PathfindResult {

  const startTX = worldToTile(fromX);
  const startTY = worldToTile(fromY);
  const goalTX = worldToTile(toX);
  const goalTY = worldToTile(toY);

  // Same tile — no path needed
  if (startTX === goalTX && startTY === goalTY) {
    return { path: [{ x: toX, y: toY }], nodesSearched: 0, outcome: 'same_tile' };
  }

  // Goal is a wall or void — no valid path
  const goalTile = groundLayer.getTileAt(goalTX, goalTY);
  if (tileBlocksMovement(goalTile)) {
    return { path: null, nodesSearched: 0, outcome: 'goal_blocked' };
  }

  const open: PathNode[] = [];
  const closed = new Set<number>();
  // #10: Map for O(1) open set lookup instead of Array.find() per neighbor
  const openMap = new Map<number, PathNode>();

  const startNode: PathNode = {
    x: startTX, y: startTY,
    g: 0,
    h: heuristic(startTX, startTY, goalTX, goalTY),
    f: 0,
    parent: null,
  };
  startNode.f = startNode.g + startNode.h;
  open.push(startNode);
  openMap.set(tileKey(startTX, startTY), startNode);

  let nodesSearched = 0;

  while (open.length > 0) {
    if (++nodesSearched > maxSearchNodes) return { path: null, nodesSearched, outcome: 'budget_exhausted' };

    // Find lowest f in open list
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i].f < open[bestIdx].f) bestIdx = i;
    }
    const current = open[bestIdx];
    open.splice(bestIdx, 1);

    const currentKey = tileKey(current.x, current.y);
    openMap.delete(currentKey);

    if (current.x === goalTX && current.y === goalTY) {
      return { path: reconstructPath(current), nodesSearched, outcome: 'found' };
    }

    closed.add(currentKey);

    // 8-directional neighbors
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;

        const nx = current.x + dx;
        const ny = current.y + dy;
        const nKey = tileKey(nx, ny);

        if (closed.has(nKey)) continue;

        // Check walkability — null means no tile (void), collides means wall
        const tile = groundLayer.getTileAt(nx, ny);
        if (tileBlocksMovement(tile)) continue;

        // Diagonal: block if either adjacent cardinal is a wall (no corner cutting)
        if (dx !== 0 && dy !== 0) {
          const adjX = groundLayer.getTileAt(current.x + dx, current.y);
          const adjY = groundLayer.getTileAt(current.x, current.y + dy);
          if (tileBlocksMovement(adjX) || tileBlocksMovement(adjY)) continue;
        }

        // Buffer-blocked tiles are walkable but costly — enemies prefer non-buffer routes
        const baseCost = (dx !== 0 && dy !== 0) ? 1.414 : 1;
        const bufferCost = tile?.properties?.bufferBlocked ? BUFFER_TILE_COST_MULTIPLIER : 1;
        const moveCost = (pathSeed !== undefined && perturbStrength)
          ? baseCost * bufferCost * (1 + perturbStrength * (perturbNoise(nx, ny, pathSeed) - 0.5))
          : baseCost * bufferCost;
        const tentativeG = current.g + moveCost;

        // Check if already in open with better g — O(1) lookup via Map
        const existing = openMap.get(nKey);
        if (existing) {
          if (tentativeG < existing.g) {
            existing.g = tentativeG;
            existing.f = tentativeG + existing.h;
            existing.parent = current;
          }
          continue;
        }

        const h = heuristic(nx, ny, goalTX, goalTY);
        const newNode: PathNode = {
          x: nx, y: ny,
          g: tentativeG,
          h,
          f: tentativeG + h,
          parent: current,
        };
        open.push(newNode);
        openMap.set(nKey, newNode);
      }
    }
  }

  return { path: null, nodesSearched, outcome: 'unreachable' as PathfindOutcome };
}

function heuristic(ax: number, ay: number, bx: number, by: number): number {
  // Octile distance (allows diagonal movement)
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  return Math.max(dx, dy) + 0.414 * Math.min(dx, dy);
}

function reconstructPath(node: PathNode): Position[] {
  const path: Position[] = [];
  let current: PathNode | null = node;

  while (current) {
    path.push({
      x: tileToWorld(current.x),
      y: tileToWorld(current.y),
    });
    current = current.parent;
  }

  path.reverse();
  // Skip the first node (enemy's current tile)
  if (path.length > 1) path.shift();
  return path;
}
